/**
 * Aspect — SceneManager (Day 7 stub)
 * --------------------------------------------------------------
 * Consumes scene commands routed from the bridge (via NetworkManager app
 * events) and maintains a lightweight client-side mirror of placements. On this
 * day it only logs/acks and tracks records; Day 8-10 upgrade it to build room
 * geometry and instantiate pooled glTF models.
 *
 * Decoupling: this listens to `net:*` app events and never reaches into the
 * socket directly. It announces `viewport:ready` (via the NetworkManager) once
 * the connection is live so the MCP server can hydrate it.
 */

import { fullRebuild, surfaceOptionsForRoom } from "./scene/room_geometry.js";
import { applyFinish, wallTiling } from "./scene/texture_sets.js";
import { DEFAULT_FLOOR_COUNT, DEFAULT_ACTIVE_FLOOR } from "./scene/floor_defaults.js";
import { SceneStore } from "./scene/scene_store.js";
import { SnapshotGate } from "./scene/snapshot_gate.js";
import { WallCollision } from "./scene/wall_collision.js";
import { AssetRegistry } from "./assets/asset_registry.js";
import { ObjectPool } from "./assets/object_pool.js";
import { applyPlacementTransform, worldPlacementPosition } from "./scene/transforms.js";
import { structureBudget } from "./scene/structure_pricing.js";
import { aspectSalePrice } from "./currency.js";
import { syncContactShadow, removeContactShadow } from "./scene/contact_shadows.js";
import { saveSessionTour, clearSessionTour } from "./session_tour_store.js";
import { placeAmbientShadowLight } from "./scene/environment.js";
import { isTouchUi, safariMaxLiveFurniture } from "./device_profile.js";

const BEDROOM_NAME_RE = /bedroom/i;
const MOBILE_BED_MARGIN = 1.1;

function findBedroomSubroom(room) {
  const list = Array.isArray(room?.rooms) ? room.rooms : [];
  return (
    list.find((r) => BEDROOM_NAME_RE.test(String(r.name ?? ""))) ||
    list.find((r) => BEDROOM_NAME_RE.test(String(r.type ?? "")))
  );
}

/** Liang-Barsky clip of a 2D segment to an axis-aligned rectangle. */
function clipSegToRect(ax, az, bx, bz, x0, z0, x1, z1) {
  let t0 = 0;
  let t1 = 1;
  const dx = bx - ax;
  const dz = bz - az;
  const clip = (p, q) => {
    if (Math.abs(p) < 1e-9) return q >= -1e-9;
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };
  if (!clip(-dx, ax - x0)) return null;
  if (!clip(dx, x1 - ax)) return null;
  if (!clip(-dz, az - z0)) return null;
  if (!clip(dz, z1 - az)) return null;
  if (t1 - t0 < 1e-4) return null;
  return {
    start: [ax + t0 * dx, az + t0 * dz],
    end: [ax + t1 * dx, az + t1 * dz],
  };
}

function xzInRect(x, z, x0, z0, x1, z1, m = 0) {
  return x >= x0 - m && x <= x1 + m && z >= z0 - m && z <= z1 + m;
}

function lightXZ(l) {
  const pos = l?.position;
  if (Array.isArray(pos) && pos.length >= 2) {
    return [Number(pos[0]) || 0, Number(pos[pos.length >= 3 ? 2 : 1]) || 0];
  }
  return [Number(l?.x) || 0, Number(l?.z) || 0];
}

/**
 * Phones only: drop every room except the Bedroom (walls, openings, lights,
 * envelope). Desktop keeps the full home. Interior doors are sealed so the
 * player cannot walk into unloaded space.
 */
function clipRoomToBedroom(room) {
  if (!room) return room;
  const bed = findBedroomSubroom(room);
  const ceilH = room.dimensions?.height ?? 2.8;
  if (!bed) {
    return { ...room, floorCount: 1 };
  }
  const origMin = room.bounds?.min ?? [0, 0, 0];
  const origMax = room.bounds?.max ?? [0, ceilH, 0];
  const bw = Number(bed.width) || 0;
  const bd = Number(bed.depth) || 0;
  if (bw < 1.5 || bd < 1.5) {
    return { ...room, floorCount: 1 };
  }

  const x0 = origMin[0] + (Number(bed.x) || 0);
  const z0 = origMin[2] + (Number(bed.z) || 0);
  const x1 = x0 + bw;
  const z1 = z0 + bd;
  const wallPad = 0.12;

  const walls = [];
  for (const w of room.walls ?? []) {
    if (!Array.isArray(w.start) || !Array.isArray(w.end)) continue;
    const clipped = clipSegToRect(
      w.start[0],
      w.start[1],
      w.end[0],
      w.end[1],
      x0 - wallPad,
      z0 - wallPad,
      x1 + wallPad,
      z1 + wallPad,
    );
    if (!clipped) continue;
    const len = Math.hypot(clipped.end[0] - clipped.start[0], clipped.end[1] - clipped.start[1]);
    if (len < 0.2) continue;
    walls.push({ ...w, start: clipped.start, end: clipped.end });
  }
  const wallIds = new Set(walls.map((w) => w.id));

  const openings = (room.openings ?? []).filter((o) => {
    if (!o) return false;
    if (o.wall && !wallIds.has(o.wall)) return false;
    const c = o.center;
    const ox = Array.isArray(c) ? Number(c[0]) || 0 : 0;
    const oz = Array.isArray(c) ? Number(c[2]) || 0 : 0;
    if (!xzInRect(ox, oz, x0, z0, x1, z1, 0.4)) return false;
    const isDoor = String(o.type ?? "").toLowerCase().includes("door");
    if (isDoor && String(o.wall ?? "").startsWith("w_int")) return false;
    return true;
  });

  const inBed = (x, z) => xzInRect(x, z, x0, z0, x1, z1, 0.35);
  const lights = (room.lights ?? []).filter((l) => {
    const [lx, lz] = lightXZ(l);
    return inBed(lx, lz);
  });
  const switches = (room.switches ?? []).filter((s) => {
    const [sx, sz] = lightXZ(s);
    return inBed(sx, sz);
  });

  const y0 = origMin[1] ?? 0;
  const y1 = origMax[1] ?? ceilH;

  return {
    ...room,
    name: bed.name ?? "Bedroom",
    floorCount: 1,
    bounds: { min: [x0, y0, z0], max: [x1, y1, z1] },
    dimensions: {
      ...(room.dimensions ?? {}),
      width: bw,
      depth: bd,
      height: room.dimensions?.height ?? ceilH,
    },
    walls,
    openings,
    lights,
    switches,
    rooms: [
      {
        ...bed,
        x: 0,
        z: 0,
        width: bw,
        depth: bd,
        name: bed.name ?? "Bedroom",
        type: bed.type ?? "bedroom",
      },
    ],
    floors: [{ id: "f_bedroom", y: 0, polygon: [[x0, z0], [x1, z0], [x1, z1], [x0, z1]] }],
  };
}

export class SceneManager {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {import('./network.js').NetworkManager} net
   * @param {object} [deps] { camera, skyTexture }
   */
  constructor(app, net, deps = {}) {
    this.app = app;
    this.net = net;
    this.camera = deps.camera ?? null;
    this.skyTexture = deps.skyTexture ?? null;
    this.lightingRoot = deps.lightingRoot ?? null;
    this.roomRoot = null; // current room geometry entity (for teardown)
    /** Session-local edits; shared default tour is never written. */
    this.sandbox = Boolean(deps.sandbox);
    this._sandboxSealed = false;
    /** After Reset, ignore a late 2D snapshot until the next upload. */
    this._acceptSandboxSnapshots = true;
    this._sessionSnapshot = null;

    /**
     * Single source of truth for room + placements + catalog on the client.
     * All mutations flow through the store; derived systems (walk collision,
     * plan editor mirror, minimap) subscribe to its change events.
     */
    this.store = new SceneStore();

    this.registry = new AssetRegistry(app);
    this.pool = new ObjectPool(app, this.registry);

    /** @type {Map<string, import('playcanvas').Entity>} placement id -> entity */
    this.entities = new Map();

    /** Wall collision resolver (rebuilt when the room or doors change). */
    this.wallCollision = new WallCollision();

    // Physics derives from the SAME placement records as rendering: any store
    // change rebuilds walk obstacles, so colliders can never drift from meshes.
    this.store.on("placements", () => {
      this.wallCollision.rebuildObstacles(this.store.placements.values());
      this._schedulePersistSession();
    });
    this.store.on("room", () => this._schedulePersistSession());

    // Bumped on every room (re)load. Async spawns that resolve after a reload are
    // stale and must not leak pooled entities into the new room (multi-agent race).
    this._generation = 0;

    /** Pending spawn tasks keyed by placement id (prevents duplicate pool instances). */
    this._spawnInflight = new Map();

    /** Serializes full scene resyncs so overlapping loads cannot drop furniture. */
    this._resyncQueue = Promise.resolve();

    /**
     * Snapshot idempotency gate. Snapshots from the server carry a monotonic
     * `version` plus a per-process `syncId` epoch. A snapshot with the same
     * epoch and version <= the last applied one is stale (WS-vs-HTTP race,
     * duplicate resync) and is dropped instead of tearing the room down again.
     */
    this._snapshotGate = new SnapshotGate();

    this._bind();
  }

  // --- Store delegation (existing consumers read scene.room / scene.placements) ---

  get room() {
    return this.store.room;
  }

  set room(v) {
    this.store.setRoom(v);
  }

  get placements() {
    return this.store.placements;
  }

  get catalog() {
    return this.store.catalog;
  }

  _bind() {
    // Announce ourselves once the bridge handshake completes (initial + reconnect).
    // Reset the snapshot gate: after a (re)connect the local scene may have
    // drifted, so the next snapshot must apply even if its version is unchanged.
    // The WS resync and the HTTP net:ready pull still dedupe against EACH OTHER
    // because the first one through the queue re-arms the gate.
    this.app.on("net:ready", () => {
      if (this._sandboxSealed) return;
      this._snapshotGate.reset();
      this.net.notify("viewport:ready", {});
    });

    this.app.on("net:scene:load_room", (room) => this.onLoadRoom(room));
    this.app.on("net:scene:catalog", (c) => this.onCatalog(c));
    this.app.on("net:furniture:spawn", (p) => this.onSpawn(p));
    this.app.on("net:furniture:update_transform", (p) => this.onUpdateTransform(p));
    this.app.on("net:furniture:remove", (p) => this.onRemove(p));
    // WS resync and HTTP fallback both funnel through applySnapshot: one queue,
    // one version gate — a snapshot can never apply twice or out of order.
    this.app.on("net:scene:resync", (s) => {
      void this.applySnapshot(s);
    });
    this.app.on("scene:sandbox-expect", () => this.expectSandboxSnapshot());
    this.app.on("scene:sandbox-unseal", () => this.unsealSandbox());
    this.app.on("scene:reapply-session", () => {
      if (this._sessionSnapshot) void this.applySnapshot(this._sessionSnapshot);
    });

    // Surface finish picks from the UI: apply locally in place (no rebuild)
    // and stream the choice to the server so it survives resync/reload.
    this.app.on("ui:set-finish", ({ role, finishId }) => {
      if (!role || !finishId) return;
      const finishes = { [role]: finishId };
      this.applyFinishes(finishes);
      // Read-only tour clients preview finishes locally but never write back.
      // Read-only and sandbox tours preview finishes locally but never write the shared world.
      if (!this.readOnly && !this.sandbox) this.net.notify("viewport:finish_patch", { finishes });
    });
  }

  /**
   * Apply surface finishes (wall / floor / floorWet) to the live room: patch
   * the room record (so rebuilds keep the choice) and re-target the shared
   * materials in place — no geometry rebuild, every mesh keeps its material.
   * @param {{ wall?: string, floor?: string, floorWet?: string }} finishes
   */
  applyFinishes(finishes = {}) {
    if (!this.room) return;
    const clean = {};
    for (const role of ["wall", "floor", "floorWet"]) {
      if (typeof finishes[role] === "string" && finishes[role]) clean[role] = finishes[role];
    }
    if (!Object.keys(clean).length) return;
    this.store.patchRoom((room) => {
      room.finishes = { ...(room.finishes ?? {}), ...clean };
    });

    const surf = this.roomRoot?._aspectSurf;
    if (surf) {
      const { width, depth, height } = surfaceOptionsForRoom(this.room);
      if (clean.wall) void applyFinish(this.app, surf.wall, clean.wall, { tiling: wallTiling(height) });
      if (clean.floor) void applyFinish(this.app, surf.floorWood, clean.floor, { tileW: width, tileD: depth });
      if (clean.floorWet) void applyFinish(this.app, surf.floorTile, clean.floorWet, { tileW: width, tileD: depth });
    }
    this.app.fire("scene:finishes-changed", { finishes: this.room.finishes });
  }

  /**
   * Apply a full scene snapshot (room + placements + catalog). All snapshot
   * sources (WS resync, HTTP /viewport-state) MUST enter through here so
   * applications are serialized and version-gated.
   * @returns {Promise<boolean>} true when the snapshot was applied (not stale)
   */
  applySnapshot(state) {
    const task = this._resyncQueue.then(() => this._applyResync(state));
    this._resyncQueue = task.then(
      () => undefined,
      (err) => console.error("[scene] resync failed", err),
    );
    return task;
  }


  onCatalog(c) {
    this.store.setCatalog(c?.assets);
    console.info("[scene] catalog", this.catalog.size, "assets");
    this.app.fire("scene:catalog-loaded", [...this.catalog.values()]);
  }

  /** Sum of furniture placement prices. */
  furnitureCost() {
    let sum = 0;
    for (const p of this.placements.values()) sum += p.price ?? 0;
    return sum;
  }

  /** Furniture + doors, windows, lights, and switches. */
  totalCost() {
    const structure = structureBudget(this.room);
    return this.furnitureCost() + structure.total;
  }

  /** Named sub-room containing a world XZ point (smallest wins), or null. */
  roomAt(wx, wz) {
    const rooms = this.room?.rooms;
    if (!Array.isArray(rooms) || !rooms.length) return null;
    const min = this.room.bounds?.min ?? [0, 0, 0];
    let best = null;
    let bestArea = Infinity;
    for (const r of rooms) {
      const rw = Number(r.width) || 0;
      const rd = Number(r.depth) || 0;
      if (rw < 0.2 || rd < 0.2) continue;
      const x0 = min[0] + (Number(r.x) || 0);
      const z0 = min[2] + (Number(r.z) || 0);
      if (wx < x0 || wx > x0 + rw || wz < z0 || wz > z0 + rd) continue;
      const area = rw * rd;
      if (area < bestArea) {
        bestArea = area;
        best = { name: r.name ?? r.type ?? "Room", x0, z0, x1: x0 + rw, z1: z0 + rd };
      }
    }
    return best;
  }

  /** Track the room the player is standing in; re-scopes the budget HUD. */
  setCurrentRoom(room) {
    const prev = this._currentRoom?.name ?? null;
    const next = room?.name ?? null;
    if (prev === next) return;
    this._currentRoom = room ?? null;
    this.app.fire("ui:room-changed", { name: next });
    this._emitBudget();
  }

  /**
   * Per-room Aspect savings rows for the minimap-side panel: every named room
   * with furniture gets { name, list, sale, pct, count }, sorted by savings.
   */
  _emitRoomSavings() {
    const rooms = this.room?.rooms;
    const min = this.room?.bounds?.min ?? [0, 0, 0];
    if (!Array.isArray(rooms) || !rooms.length) {
      this.app.fire("ui:room-savings", { rows: [] });
      return;
    }
    const M = 0.25;
    const rects = [];
    for (const r of rooms) {
      const rw = Number(r.width) || 0;
      const rd = Number(r.depth) || 0;
      if (rw < 0.2 || rd < 0.2) continue;
      const x0 = min[0] + (Number(r.x) || 0);
      const z0 = min[2] + (Number(r.z) || 0);
      rects.push({
        name: r.name ?? r.type ?? "Room",
        x0,
        z0,
        x1: x0 + rw,
        z1: z0 + rd,
        area: rw * rd,
        list: 0,
        sale: 0,
        count: 0,
      });
    }
    for (const p of this.placements.values()) {
      const [x, , z] = p.position ?? [0, 0, 0];
      // Smallest containing rect wins (mirrors roomAt) so hallway-spanning
      // rooms don't swallow items from the rooms they border.
      let best = null;
      for (const r of rects) {
        if (x < r.x0 - M || x > r.x1 + M || z < r.z0 - M || z > r.z1 + M) continue;
        if (!best || r.area < best.area) best = r;
      }
      if (!best) continue;
      const price = p.price ?? 0;
      best.list += price;
      best.sale += aspectSalePrice(price, p.id)?.sale ?? price;
      best.count += 1;
    }
    const rows = rects
      .map((r) => ({
        name: r.name,
        list: Math.round(r.list),
        sale: Math.round(r.sale),
        pct: r.list > 0 ? Math.max(0, Math.round((1 - r.sale / r.list) * 100)) : 0,
        count: r.count,
      }))
      .sort((a, b) => b.list - b.sale - (a.list - a.sale));
    this.app.fire("ui:room-savings", { rows });
  }

  /**
   * Budget HUD: scoped to the room the player is in (furniture inside its
   * rect). Falls back to the whole home (furniture + structure) when the
   * player is between rooms or in overhead view.
   */
  _emitBudget() {
    this._emitRoomSavings();
    const room = this._currentRoom;
    if (room) {
      const M = 0.25; // wall-hugging items straddle the rect edge slightly
      let total = 0;
      let sale = 0;
      let count = 0;
      for (const p of this.placements.values()) {
        const [x, , z] = p.position ?? [0, 0, 0];
        if (x < room.x0 - M || x > room.x1 + M || z < room.z0 - M || z > room.z1 + M) continue;
        const price = p.price ?? 0;
        total += price;
        sale += aspectSalePrice(price, p.id)?.sale ?? price;
        count += 1;
      }
      this.app.fire("ui:budget-changed", { total, saleTotal: sale, count, room: room.name });
      return;
    }

    const structure = structureBudget(this.room);
    // Sale sum mirrors the per-item hover-card discount (stable key = placement id).
    let furnitureSale = 0;
    for (const p of this.placements.values()) {
      const price = p.price ?? 0;
      furnitureSale += aspectSalePrice(price, p.id)?.sale ?? price;
    }
    this.app.fire("ui:budget-changed", {
      total: this.furnitureCost() + structure.total,
      saleTotal: furnitureSale + (structure.saleTotal ?? structure.total),
      count: this.placements.size + structure.count,
      room: null,
    });
  }

  /**
   * Signal that placement records changed (in-place mutations included).
   * The store subscription rebuilds walk obstacles from the live records.
   */
  _syncObstacleCollision() {
    this.store.touchPlacements();
  }

  /** Normalize a placement record into world-space Y for the active storey. */
  _worldPlacement(p) {
    if (!p) return p;
    const floorY = this.room?.floorY ?? 0;
    const pos = worldPlacementPosition(p.position, floorY);
    const enriched = this._enrichPlacement(p);
    // Stacked tabletop items keep their authored Y (TV on a stand, lamp on a nightstand).
    if (p.parent_id) return { ...enriched, position: pos };
    // Ceiling / wall fixtures sit at an authored height — storey snap would
    // drop thin pucks (y ≈ ceiling) onto the floor.
    if (enriched.placement_mode === "ceiling" || enriched.placement_mode === "wall") {
      return { ...enriched, position: pos };
    }
    return { ...enriched, position: this._snapPlacementY(pos, floorY) };
  }

  /**
   * Keep furniture on the live floor. Server poses are lifted to storey 3
   * (~5.2 m); phones must not leave that Y while the room mesh sits elsewhere.
   */
  _snapPlacementY(position, floorY) {
    if (!Array.isArray(position) || position.length < 3) return position;
    const y = Number(position[1]) || 0;
    const ceilH = this.room?.dimensions?.height ?? 2.8;
    const storey = Math.floor((y + 0.05) / Math.max(0.5, ceilH)) * ceilH;
    if (Math.abs(storey - floorY) <= 0.25) return position;
    return [position[0], floorY + (y - storey), position[2]];
  }

  /** Fill missing spawn fields from the local catalog mirror. */
  _enrichPlacement(p) {
    if (!p) return p;
    const meta = this.catalog.get(p.asset_id);
    return {
      ...p,
      glb: p.glb ?? meta?.glb ?? (p.asset_id ? `/assets/models/${p.asset_id}.glb` : undefined),
      footprint: p.footprint ?? meta?.footprint ?? { w: 1, h: 0.8, d: 1 },
      category: p.category ?? meta?.category ?? "default",
      price: p.price ?? meta?.price ?? 0,
      ...(meta?.supports_surface || p.supports_surface ? { supports_surface: true } : {}),
      ...(meta?.placement_mode || p.placement_mode
        ? { placement_mode: p.placement_mode ?? meta.placement_mode }
        : {}),
    };
  }

  /**
   * Drop extra pooled instances for one placement (async spawn races or stale ghosts).
   * Keeps `keepEntity` as the sole enabled instance for `id`.
   */
  _purgePlacementDuplicates(id, keepEntity) {
    if (!id || !this.pool?.root) return;

    for (const child of [...this.pool.root.children]) {
      if (child === keepEntity) continue;
      if (child._aspectPlacementId !== id) continue;
      child._aspectPlacementId = undefined;
      this.pool.release(child);
    }
  }

  /** Refresh walk obstacles after a viewport-only pose change (no server echo). */
  syncWalkObstacles() {
    this._syncObstacleCollision();
  }

  /**
   * Swap a placement's asset in place (keep id + transform). Releases the old
   * pooled instance, acquires the new asset, and notifies the server.
   */
  async swap(id, newAssetId) {
    const rec = this.placements.get(id);
    if (!rec) return;
    const meta = this.catalog.get(newAssetId);
    const oldEntity = this.entities.get(id);
    if (oldEntity) {
      this.pool.release(oldEntity);
      this.entities.delete(id);
    }
    const updated = this._worldPlacement({
      ...rec,
      asset_id: newAssetId,
      glb: `/assets/models/${newAssetId}.glb`,
      footprint: meta?.footprint ?? rec.footprint,
      price: meta?.price ?? rec.price,
      category: meta?.category,
    });
    this.store.upsertPlacement(updated);
    const entity = await this.pool.acquire(updated.asset_id, updated.glb, updated.footprint, updated.category);
    entity._aspectPlacementId = id;
    this.entities.set(id, entity);
    applyPlacementTransform(entity, updated);
    this.registry.prepareDrawable(entity, entity._aspectContainerResource ?? null);
    syncContactShadow(entity, updated, this.app);
    this._purgePlacementDuplicates(id, entity);

    if (!this.sandbox) this.net.notify("viewport:swapped", { id, asset_id: newAssetId });
    this._emitBudget();
    this.app.fire("scene:placement-updated", updated);
    console.info("[scene] swap", id, "->", newAssetId);
  }

  onLoadRoom(room) {
    this.room = this._roomForBuild(room);
    this._generation++;
    const mobileClip = isTouchUi() ? ` bedroom-only ${this.room?.walls?.length ?? 0} walls` : "";
    console.info("[scene] load_room", room?.id, room?.dimensions, `floor ${this.room.activeFloor}/${this.room.floorCount}${mobileClip}`);

    // Tear down any previous room geometry before rebuilding (no leak/stacking).
    if (this.roomRoot) {
      this.roomRoot.destroy();
      this.roomRoot = null;
    }
    // Fresh room => release all pooled furniture instances and clear mirrors.
    this.pool.releaseAll();
    this.entities.clear();
    this._deferred?.clear();
    this.store.clearPlacements();
    this._syncObstacleCollision();

    this.roomRoot = fullRebuild(this.app, this.room, { skyTexture: this.skyTexture });
    placeAmbientShadowLight(this.lightingRoot, this.room);
    this.app.renderNextFrame = true;
    // FPS controls reposition the camera when the room loads.

    this.app.fire("scene:room-loaded", this.room);
    this._emitBudget();
  }

  /**
   * Patch door/window records on the live room (plan editor — no full rebuild).
   * @param {{ upsert?: object[], remove?: string[] }} patch
   * @returns {string[]} affected wall ids
   */
  applyOpeningPatch(patch) {
    const upsert = Array.isArray(patch?.upsert) ? patch.upsert : [];
    const remove = Array.isArray(patch?.remove) ? patch.remove : [];
    if (!this.room) return [];
    const removedWallIds = [];
    this.store.patchRoom((room) => {
      const byId = new Map((room.openings ?? []).map((o) => [o.id, o]));
      for (const id of remove) {
        const prev = byId.get(id);
        if (prev?.wall) removedWallIds.push(prev.wall);
        byId.delete(id);
      }
      for (const op of upsert) {
        if (!op?.id) continue;
        byId.set(op.id, { ...op });
      }
      room.openings = [...byId.values()];
    });
    const wallIds = [
      ...new Set([
        ...upsert.map((o) => o.wall).filter(Boolean),
        ...removedWallIds,
      ]),
    ];
    this.app.fire("scene:openings-patched", { wallIds, upsert, remove });
    this._emitBudget();
    return wallIds;
  }

  /**
   * Patch wall segments on the live room (plan editor — no full rebuild).
   * @param {{ upsert?: object[], remove?: string[] }} patch
   */
  applyTopologyPatch(patch) {
    const upsert = Array.isArray(patch?.upsert) ? patch.upsert : [];
    const remove = Array.isArray(patch?.remove) ? patch.remove : [];
    if (!this.room) return { wallIds: [], removeWallIds: [], removedOpeningIds: [] };

    const removeSet = new Set(remove);
    const removedOpeningIds = (this.room.openings ?? [])
      .filter((o) => removeSet.has(o.wall))
      .map((o) => o.id);

    this.store.patchRoom((room) => {
      room.openings = (room.openings ?? []).filter((o) => !removeSet.has(o.wall));

      const byId = new Map((room.walls ?? []).map((w) => [w.id, w]));
      for (const id of remove) byId.delete(id);
      for (const w of upsert) {
        if (!w?.id) continue;
        byId.set(w.id, { ...byId.get(w.id), ...w });
      }
      room.walls = [...byId.values()];
    });

    const wallIds = [...new Set(upsert.map((w) => w.id).filter(Boolean))];
    this.app.fire("scene:topology-patched", { wallIds, removeWallIds: remove, removedOpeningIds });
    this._emitBudget();
    return { wallIds, removeWallIds: remove, removedOpeningIds };
  }

  /**
   * Patch ceiling lights + wall switches on the live room (plan editor Apply).
   * @param {{ lights?: object[], switches?: object[] }} patch
   */
  applyLightsPatch(patch) {
    if (!this.room) return;
    const lights = Array.isArray(patch?.lights) ? patch.lights : [];
    const switches = Array.isArray(patch?.switches) ? patch.switches : [];
    this.store.patchRoom((room) => {
      room.lights = lights;
      room.switches = switches;
    });
    this.app.fire("scene:lights-patched", { lights, switches });
    this._emitBudget();
  }

  /** World XZ of the live bedroom envelope (phones only). */
  _bedroomWorldRect() {
    if (!isTouchUi() || !this.room?.bounds?.min || !this.room?.bounds?.max) return null;
    const { min, max } = this.room.bounds;
    return { x0: min[0], z0: min[2], x1: max[0], z1: max[2] };
  }

  _placementInBedroom(p, margin = MOBILE_BED_MARGIN) {
    if (!isTouchUi()) return true;
    const rect = this._bedroomWorldRect();
    if (!rect || !p) return false;
    const [x, z] = this._placementXZ(p);
    if (xzInRect(x, z, rect.x0, rect.z0, rect.x1, rect.z1, margin)) return true;
    return Boolean(p.parent_id && this.placements.has(p.parent_id));
  }

  /** Normalize floor fields and offset opening Y to the active storey. */
  _roomForBuild(room) {
    if (!room) return room;
    const src = isTouchUi() ? clipRoomToBedroom(room) : room;
    const ceilH = src.dimensions?.height ?? 2.8;
    let floorCount = Math.min(10, Math.max(1, Math.round(Number(src.floorCount) || DEFAULT_FLOOR_COUNT)));
    let activeFloor = Math.min(floorCount, Math.max(1, Math.round(Number(src.activeFloor) || DEFAULT_ACTIVE_FLOOR)));
    if (isTouchUi()) {
      floorCount = 1;
    }
    const storeyForY = Number(src.activeFloor) || DEFAULT_ACTIVE_FLOOR;
    const floorY = src.floorY != null && Number.isFinite(Number(src.floorY))
      ? Number(src.floorY)
      : (storeyForY - 1) * ceilH;
    const openings = (src.openings ?? []).map((o) => {
      const isDoor = String(o.type ?? "").toLowerCase().includes("door");
      const width = isDoor && Number(o.width) > 1.8 ? 1.8 : o.width;
      return {
        ...o,
        width,
        center: o.center ? [o.center[0], o.center[1] + floorY, o.center[2]] : o.center,
      };
    });
    return { ...src, floorCount, activeFloor, floorY, openings };
  }

  /** Switch the active storey (client-side geometry rebuild; persists on next Apply). */
  setActiveFloor(activeFloor) {
    if (!this.room) return;
    const floorCount = this.room.floorCount ?? 1;
    const af = Math.min(floorCount, Math.max(1, Math.round(Number(activeFloor) || 1)));
    if (af === (this.room.activeFloor ?? 1)) return;
    const ceilH = this.room.dimensions?.height ?? 2.8;
    const oldFloorY = this.room.floorY ?? 0;
    const newFloorY = (af - 1) * ceilH;
    const saved = this._snapshotPlacementsForFloorMove(oldFloorY, newFloorY);
    const base = {
      ...this.room,
      activeFloor: af,
      floorY: newFloorY,
      openings: (this.room.openings ?? []).map((o) => ({
        ...o,
        center: o.center
          ? [o.center[0], o.center[1] - oldFloorY, o.center[2]]
          : o.center,
      })),
    };
    // Local rebuild desyncs us from server snapshots — allow the next one through.
    this._snapshotGate.reset();
    this.onLoadRoom(base);
    void this._restorePlacements(saved);
  }

  /** Change total storeys and rebuild (shell floors appear above/below the active one). */
  setFloorCount(floorCount) {
    if (!this.room) return;
    const fc = Math.min(10, Math.max(1, Math.round(Number(floorCount) || 1)));
    const af = Math.min(fc, this.room.activeFloor ?? 1);
    const ceilH = this.room.dimensions?.height ?? 2.8;
    const oldFloorY = this.room.floorY ?? 0;
    const newFloorY = (af - 1) * ceilH;
    const saved = this._snapshotPlacementsForFloorMove(oldFloorY, newFloorY);
    const base = {
      ...this.room,
      floorCount: fc,
      activeFloor: af,
      openings: (this.room.openings ?? []).map((o) => ({
        ...o,
        center: o.center
          ? [o.center[0], o.center[1] - oldFloorY, o.center[2]]
          : o.center,
      })),
    };
    this._snapshotGate.reset();
    this.onLoadRoom(base);
    void this._restorePlacements(saved);
  }

  /**
   * Capture live placements with Y remapped for a storey change. Room rebuild
   * clears the pool; callers re-spawn from this list so furniture stays put.
   */
  _snapshotPlacementsForFloorMove(oldFloorY, newFloorY) {
    return [...this.placements.values()].map((p) => {
      const [x, y = 0, z] = p.position ?? [0, 0, 0];
      const localY = (Number(y) || 0) - (Number(oldFloorY) || 0);
      return {
        ...p,
        position: [x, localY + (Number(newFloorY) || 0), z],
      };
    });
  }

  /** Re-spawn placements after a local room rebuild (floor switch / storey count). */
  async _restorePlacements(saved) {
    if (!saved?.length) return;
    await Promise.all(saved.map((p) => this.onSpawn(p)));
    this._syncObstacleCollision();
    this.app.renderNextFrame = true;
  }

  async onSpawn(p) {
    if (!p?.id) return;
    if (isTouchUi() && !this._placementInBedroom(p)) return;
    if (this._spawnInflight.has(p.id)) {
      await this._spawnInflight.get(p.id);
      const entity = this.entities.get(p.id);
      if (entity) {
        const merged = this._worldPlacement({ ...this.placements.get(p.id), ...p });
        this.store.upsertPlacement(merged);
        applyPlacementTransform(entity, merged);
        this._purgePlacementDuplicates(p.id, entity);
        syncContactShadow(entity, merged, this.app);
      }
      return;
    }
    const task = this._spawnPlacement(p);
    this._spawnInflight.set(p.id, task);
    try {
      await task;
    } finally {
      this._spawnInflight.delete(p.id);
    }
  }

  async _spawnPlacement(p) {
    const gen = this._generation;
    const spawn = this._enrichPlacement(p);

    let entity = this.entities.get(spawn.id);
    if (!entity) {
      entity = await this.pool.acquire(spawn.asset_id, spawn.glb, spawn.footprint, spawn.category);
      if (gen !== this._generation) {
        this.pool.release(entity);
        return;
      }
      const raced = this.entities.get(spawn.id);
      if (raced) {
        this.pool.release(entity);
        entity = raced;
      } else {
        entity._aspectPlacementId = spawn.id;
        this.entities.set(spawn.id, entity);
      }
    }
    const wp = this._worldPlacement(spawn);
    this.store.upsertPlacement(wp);
    applyPlacementTransform(entity, wp);
    this.registry.prepareDrawable(entity, entity._aspectContainerResource ?? null);
    syncContactShadow(entity, wp, this.app);
    this._purgePlacementDuplicates(spawn.id, entity);
    this.app.renderNextFrame = true;
    console.info("[scene] spawn", spawn.id, spawn.asset_id, wp.position);
    this.app.fire("scene:placement-added", wp);
    this._emitBudget();
  }

  onUpdateTransform(p) {
    if (!p?.id) return;
    // A deferred (not yet spawned) item just moved — materialize it now.
    if (this._deferred?.delete(p.id)) void this.onSpawn(p);
    const rec = this._worldPlacement({ ...(this.placements.get(p.id) ?? { id: p.id }), ...p });
    this.store.upsertPlacement(rec);
    const entity = this.entities.get(p.id);
    if (entity) {
      applyPlacementTransform(entity, rec);
      syncContactShadow(entity, rec, this.app);
      this._purgePlacementDuplicates(p.id, entity);
    }
    console.info("[scene] update_transform", p.id, p.position);
    this.app.fire("scene:placement-updated", rec);
  }

  onRemove(p) {
    const id = p?.id;
    if (!id) return;
    this._deferred?.delete(id);
    const entity = this.entities.get(id);
    if (entity) {
      removeContactShadow(entity);
      this.pool.release(entity);
      this.entities.delete(id);
    }
    if (this.store.removePlacement(id)) {
      console.info("[scene] remove", id);
      this.app.fire("scene:placement-removed", { id });
      this._emitBudget();
    }
  }

  /** Remove a placement locally and notify the server (Del key / UI delete). */
  removePlacement(id) {
    if (!id || !this.placements.has(id)) return false;
    const childIds = [];
    for (const p of this.placements.values()) {
      if (p.parent_id === id) childIds.push(p.id);
    }
    for (const cid of childIds) this.onRemove({ id: cid });
    this.onRemove({ id });
    if (!this.sandbox) this.net?.notify("viewport:removed", { id });
    return true;
  }

  /** Accept the next session-only 2D / agent snapshot (upload or prompt). */
  expectSandboxSnapshot() {
    this._acceptSandboxSnapshots = true;
  }

  isSessionTourActive() {
    return this._sandboxSealed;
  }

  /** Drop the session tour and allow the shared default snapshot through. */
  unsealSandbox() {
    this._sandboxSealed = false;
    this._acceptSandboxSnapshots = false;
    this._sessionSnapshot = null;
    this._snapshotGate.reset();
    clearSessionTour();
  }

  _schedulePersistSession() {
    if (!this.sandbox || !this._sandboxSealed) return;
    clearTimeout(this._persistTimer);
    this._persistTimer = setTimeout(() => this._persistSessionTour(), 200);
  }

  _persistSessionTour() {
    if (!this.sandbox || !this._sandboxSealed || !this.room) return;
    const snapshot = {
      sandbox: true,
      room: this.room,
      placements: this.list().map((p) => ({
        id: p.id,
        asset_id: p.asset_id,
        position: p.position,
        rotation_y: p.rotation_y,
        scale: p.scale,
        ...(p.parent_id ? { parent_id: p.parent_id } : {}),
        glb: p.glb,
        footprint: p.footprint,
        category: p.category,
        price: p.price,
        ...(p.supports_surface ? { supports_surface: true } : {}),
        ...(p.placement_mode ? { placement_mode: p.placement_mode } : {}),
      })),
      version: Date.now(),
      syncId: this._sessionSnapshot?.syncId ?? "session",
    };
    this._sessionSnapshot = snapshot;
    saveSessionTour(snapshot);
  }

  async _applyResync(state) {
    const sessionTour = Boolean(state?.sandbox);
    if (sessionTour && !this._acceptSandboxSnapshots) {
      console.info("[scene] sandbox: ignored session snapshot (default tour is active)");
      return false;
    }
    if (this._sandboxSealed && !sessionTour) {
      console.info("[scene] sandbox: keeping session tour (ignored shared resync)");
      return false;
    }
    if (sessionTour) this._snapshotGate.reset();
    if (this._snapshotGate.isStale(state)) {
      console.info("[scene] resync skipped (stale snapshot)", state?.version);
      return false;
    }
    console.info("[scene] resync", state?.placements?.length ?? 0, "placements", `v${state?.version ?? "?"}`);
    if (state?.catalog?.assets?.length) this.onCatalog(state.catalog);
    if (state?.room) this.onLoadRoom(state.room);
    await this._streamSpawns(state?.placements ?? []);
    this._syncObstacleCollision();
    this.app.renderNextFrame = true;
    this._snapshotGate.markApplied(state);
    this._sandboxSealed = sessionTour;
    if (sessionTour) {
      this._sessionSnapshot = state;
      saveSessionTour(state);
    }
    return true;
  }

  onResync(state) {
    return this.applySnapshot(state);
  }

  _placementXZ(p) {
    return Array.isArray(p?.position)
      ? [Number(p.position[0]) || 0, Number(p.position[2]) || 0]
      : [Number(p.x) || 0, Number(p.z) || 0];
  }

  _parkEntity(id) {
    const entity = this.entities.get(id);
    if (!entity) return;
    const rec = this.placements.get(id);
    const assetId = entity._aspectAssetId || rec?.asset_id;
    removeContactShadow(entity);
    this.pool.discard(entity);
    this.entities.delete(id);
    if (rec) {
      this._deferred ??= new Map();
      this._deferred.set(id, rec);
    }
    if (assetId) {
      let still = false;
      for (const e of this.entities.values()) {
        if (e._aspectAssetId === assetId) {
          still = true;
          break;
        }
      }
      if (!still) this.registry.unloadAsset(assetId);
    }
  }

  _parkFarthestOverCap(cam, cap) {
    while (this.entities.size > cap) {
      let farId = null;
      let farD = -1;
      for (const [id, entity] of this.entities) {
        const pos = entity.getPosition?.() ?? cam;
        const d2 = (pos.x - cam.x) ** 2 + (pos.z - cam.z) ** 2;
        if (d2 > farD) {
          farD = d2;
          farId = id;
        }
      }
      if (!farId) break;
      this._parkEntity(farId);
    }
  }

  /**
   * Mobile approach loader: keep only the nearest pieces in GPU memory.
   * Safari cannot hold a furnished home; unused GLBs are destroyed and
   * re-streamed when you walk back.
   */
  _ensureApproachLoader() {
    if (this._approachBound) return;
    this._approachBound = true;
    this._approachAcc = 0;
    this.app.on("update", (dt) => {
      if (!isTouchUi()) return;
      this._approachAcc += dt ?? 0.016;
      if (this._approachAcc < 0.9) return;
      this._approachAcc = 0;
      const cam = this.camera?.getPosition?.();
      if (!cam) return;
      const cap = safariMaxLiveFurniture();
      this._parkFarthestOverCap(cam, cap);
      if (!this._deferred?.size || this.entities.size >= cap) return;
      let bestId = null;
      let bestP = null;
      let bestD = Infinity;
      for (const [id, p] of this._deferred) {
        const [x, z] = this._placementXZ(p);
        const d2 = (x - cam.x) ** 2 + (z - cam.z) ** 2;
        if (d2 < bestD) {
          bestD = d2;
          bestId = id;
          bestP = p;
        }
      }
      if (!bestId || bestD > 7 * 7) return;
      this._deferred.delete(bestId);
      void this.onSpawn(bestP);
    });
    this.app.on("cam:mode-changed", ({ mode }) => {
      if (mode !== "overhead" || !this._deferred?.size) return;
      if (isTouchUi()) return; // phones never enter overhead
      const all = [...this._deferred.values()];
      this._deferred.clear();
      for (const p of all) void this.onSpawn(p);
    });
  }

  /**
   * Unity-style streamed spawning. The room the player stands in (plus a small
   * radius) loads first and blocks, so the first rendered frame shows a fully
   * furnished room. Everything else streams in closest-first batches that are
   * re-sorted against the LIVE camera position between batches — walk toward a
   * room and its furniture jumps the queue. A room reload mid-stream (new
   * generation) abandons the stale queue.
   */
  async _streamSpawns(placements) {
    const raw = Array.isArray(placements) ? placements.filter((p) => p?.id) : [];
    const camPos = this.camera?.getPosition?.() ?? null;
    const mobile = isTouchUi();
    const gen = this._generation;
    // Phones: Bedroom furniture only. Never register other rooms even as
    // deferred GLBs — Safari cannot hold a furnished home.
    let list = mobile ? raw.filter((p) => this._placementInBedroom(p)) : raw;
    if (mobile && list.length) {
      const ids = new Set(list.map((p) => p.id));
      let grew = true;
      while (grew) {
        grew = false;
        for (const p of raw) {
          if (!p?.id || ids.has(p.id)) continue;
          if (p.parent_id && ids.has(p.parent_id)) {
            ids.add(p.id);
            grew = true;
          }
        }
      }
      list = raw.filter((p) => ids.has(p.id));
      console.info("[scene] mobile bedroom furniture", list.length, "of", raw.length);
    }

    const spawnBatched = async (items, batchSize) => {
      if (!items.length) return;
      const size = batchSize ?? items.length;
      for (let i = 0; i < items.length; i += size) {
        if (gen !== this._generation) return;
        await Promise.all(items.slice(i, i + size).map((p) => this.onSpawn(p)));
        this.app.renderNextFrame = true;
        if (mobile) await new Promise((r) => setTimeout(r, 0));
      }
    };

    // Desktop tiny scenes stay fully parallel. Phones always stream one GLB
    // at a time — even a furnished bedroom can OOM Safari in one Promise.all.
    if (mobile) {
      this.app.fire("assets:stream", { phase: "room", done: 0, total: list.length });
      await spawnBatched(list, 1);
      this.app.fire("assets:stream", { phase: "done", done: list.length, total: list.length });
      return;
    }
    if (!camPos) {
      await spawnBatched(list, list.length);
      this.app.fire("assets:stream", { phase: "done", done: list.length, total: list.length });
      return;
    }
    if (list.length <= 48) {
      await spawnBatched(list);
      this.app.fire("assets:stream", { phase: "done", done: list.length, total: list.length });
      return;
    }

    /** World XZ of a raw record — snapshots carry either position[] or x/z. */
    const xzOf = (p) =>
      Array.isArray(p.position)
        ? [Number(p.position[0]) || 0, Number(p.position[2]) || 0]
        : [Number(p.x) || 0, Number(p.z) || 0];

    const rect = this.roomAt(camPos.x, camPos.z);
    const M = 0.75; // wall-hugging pieces straddle the room rect slightly
    const NEAR_R2 = 9 * 9;
    const near = [];
    const rest = [];
    for (const p of list) {
      const [x, z] = xzOf(p);
      const inRoom =
        rect && x >= rect.x0 - M && x <= rect.x1 + M && z >= rect.z0 - M && z <= rect.z1 + M;
      const d2 = (x - camPos.x) ** 2 + (z - camPos.z) ** 2;
      if (inRoom || d2 <= NEAR_R2) near.push(p);
      else rest.push(p);
    }
    this.app.fire("assets:stream", { phase: "room", done: 0, total: list.length });

    await spawnBatched(near, near.length);
    this.app.renderNextFrame = true;
    let done = near.length;
    this.app.fire("assets:stream", { phase: "world", done, total: list.length });

    // Phase 2 — the rest of the home, nearest-first against the live camera.
    const BATCH = 24;
    while (rest.length) {
      if (gen !== this._generation) return; // room rebuilt — stream is stale
      const cp = this.camera?.getPosition?.() ?? camPos;
      rest.sort((a, b) => {
        const [ax, az] = xzOf(a);
        const [bx, bz] = xzOf(b);
        return (
          (ax - cp.x) ** 2 + (az - cp.z) ** 2 - ((bx - cp.x) ** 2 + (bz - cp.z) ** 2)
        );
      });
      const batch = rest.splice(0, BATCH);
      await Promise.all(batch.map((p) => this.onSpawn(p)));
      done += batch.length;
      this.app.renderNextFrame = true;
      this.app.fire("assets:stream", { phase: rest.length ? "world" : "done", done, total: list.length });
      // Yield to the frame loop so GLB decode never stalls input.
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  list() {
    return [...this.placements.values()];
  }

  /**
   * Export live placements for Admin Save (server expects x/z/yOffset form).
   * @returns {object[]}
   */
  exportAdminPlacements() {
    const floorY = this.room?.floorY ?? 0;
    return [...this.placements.values()].map((p) => {
      const [x = 0, y = floorY, z = 0] = p.position ?? [0, floorY, 0];
      return {
        id: p.id,
        asset_id: p.asset_id,
        x: Number(x) || 0,
        z: Number(z) || 0,
        yOffset: (Number(y) || 0) - floorY,
        rotation_y: Number(p.rotation_y) || 0,
        scale: Number(p.scale) > 0 ? Number(p.scale) : 1,
        ...(p.parent_id ? { parent_id: p.parent_id } : {}),
      };
    });
  }
}

export default SceneManager;
